import { cn } from "../../utils";

export type TypoGraphyFont = "sans" | "grotesk";
export type TypographySize =
  | 8
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 20
  | 18
  | 24
  | 32;
export type TypographyVariant = "primary" | "secondary";
export type TypographyElement =
  | "h1"
  | "h2"
  | "h3"
  | "p"
  | "span"
  | "small"
  | "label";
export type TypographyWeight =
  | "light"
  | "normal"
  | "medium"
  | "semibold"
  | "bold";
export type TypographySpacing = "normal" | "narrow" | "wide";

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  ref?: React.Ref<HTMLElement | null>;
  id?: string;
  font?: TypoGraphyFont;
  bold?: boolean;
  size?: TypographySize;
  wide?: boolean;
  light?: boolean;
  medium?: boolean;
  narrow?: boolean;
  weight?: TypographyWeight;
  primary?: boolean;
  grotesk?: boolean;
  variant?: TypographyVariant;
  element?: TypographyElement;
  spacing?: TypographySpacing;
  semibold?: boolean;
  children: React.ReactNode;
  className?: string;
  xs?: boolean;
}

export const Typography: React.FC<TypographyProps> = ({
  id,
  font,
  bold,
  size,
  wide,
  light,
  medium,
  narrow,
  weight,
  primary,
  grotesk,
  variant,
  element = "span",
  spacing,
  semibold,
  children,
  className,
  xs,
  ...rest
}) => {
  const Tag = element as React.ElementType;
  const isHeading = ["h1", "h2", "h3"].includes(element);

  return (
    <Tag
      {...rest}
      id={id}
      className={cn(
        {
          "ui:font-sans": font === "sans", // default globally
          "ui:font-grotesk": font === "grotesk" || grotesk,
        },
        {
          "ui:text-[32px]": element === "h1" || size === 32,
          "ui:text-2xl": element === "h2" || size === 24,
          "ui:text-xl": element === "h3" || size === 20,
          "ui:text-lg": size === 18 && !isHeading,
          "ui:text-base": size === 16 && !isHeading,
          "ui:text-[15px]": size === 15 && !isHeading,
          "ui:text-sm": size === 14 && !isHeading, // default globally
          "ui:text-[13px]": size === 13 && !isHeading,
          "ui:text-xs": (size === 12 || xs) && !isHeading,
          "ui:text-[11px]": size === 11 && !isHeading,
          "ui:text-xxs": size === 10 && !isHeading,
          "ui:text-[8px]": size === 8 && !isHeading,
        },
        {
          "ui:text-textPrimary": isHeading || variant === "primary" || primary,
          "ui:text-textSecondary": variant === "secondary" && !isHeading, // default globally
        },
        {
          "ui:font-light": weight === "light" || light,
          "ui:font-normal": weight === "normal", // default globally
          "ui:font-medium": weight === "medium" || medium,
          "ui:font-semibold": weight === "semibold" || semibold,
          "ui:font-bold": weight === "bold" || bold,
        },
        {
          "ui:tracking-[-0.02em]": spacing === "normal", // default globally
          "ui:tracking-[-0.04em]": spacing === "narrow" || isHeading || narrow,
          "ui:tracking-[0.05em]": spacing === "wide" || wide,
        },
        className,
      )}
    >
      {children}
    </Tag>
  );
};
